import React from 'react';
import { useIntl } from 'react-intl';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRouteNames, SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { OutreachShell } from '../components/OutreachShell';
import { OutreachPlanCard } from '../components/OutreachPlanCard';
import { useArtistName } from '../hooks/useArtistName';
import { useOutreachHome } from '../hooks/useOutreachHome';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { ProfileStateBar } from '../profile/ProfileStateBar';
import { HomeActivity } from './HomeActivity';
import { HomeCampaigns } from './HomeCampaigns';
import { HomeFirstPitch } from './HomeFirstPitch';
import { HomeMailboxes } from './HomeMailboxes';
import { HomeRateCard } from './HomeRateCard';
import { HomeTodayHero } from './HomeTodayHero';
import { HomeTotals } from './HomeTotals';
import { homeMessages as messages } from './homeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Home.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Outreach → Pitch (design outreach-v2 i1, i2). With no campaign, the first visit (i2) offers the
 * song to pitch. Else: today of the artist against the daily rate (the hero), the totals, the songs
 * that Plug pitches with the rate of each, the latest events, the rate card (the Ultra offer below
 * Ultra) and the mailboxes. On Free the plan card sits on top. The head is the Pitch head of
 * navigation v2 (d 4a): "Pitch a song" in the title row, then the Pitch tabs.
 */
const OutreachHomeScreenComponent: React.FC<{ accountId: number }> = ({ accountId }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const plan = useOutreachPlan();
  const artist = useArtistName(accountId) ?? '';
  const { home, isLoading, error, retry } = useOutreachHome(accountId);

  const content = () => {
    if (isLoading) {
      return <LoadingView />;
    }
    if (error !== null || home === undefined) {
      return <ErrorView error={error ?? undefined} retry={retry} />;
    }
    if (home.campaigns.length === 0) {
      return (
        <>
          <ProfileStateBar accountId={accountId} />
          <HomeFirstPitch pitchSong={home.pitchSong} />
        </>
      );
    }
    return (
      <>
        {!plan.canPitch && (
          <OutreachPlanCard
            title={intl.formatMessage(messages.freePlanTitle, { count: home.matchCounts.playlists })}
            subject={home.pitchSong?.title}
          />
        )}
        <ProfileStateBar accountId={accountId} />
        {plan.canPitch && <HomeTodayHero today={home.today} artist={artist} />}
        <HomeTotals totals={home.totals28d} />
        <div className={styles.grid}>
          <div className={shared.stack}>
            <HomeCampaigns campaigns={home.campaigns} rate={home.today.rate} />
            <HomeActivity events={home.activity} />
          </div>
          <div className={shared.stack}>
            {plan.canPitch && <HomeRateCard rate={home.today.rate} artist={artist} />}
            <HomeMailboxes mailboxes={home.mailboxes} />
          </div>
        </div>
      </>
    );
  };

  return (
    <OutreachShell
      section="tabs"
      tab={SMLRouteNames.outreach}
      pitchSong={home?.pitchSong?.catalogItemUuid ?? null}
      actions={
        <>
          <SMLButton size="sm" outline omitCap leftIcon={faUser} onClick={() => router.push(SMLRoutes.outreachProfile)}>
            {intl.formatMessage(messages.yourSound)}
          </SMLButton>
          <SMLButton
            size="sm"
            outline
            omitCap
            leftIcon={faEnvelope}
            onClick={() => router.push(SMLRoutes.settings, { tab: 'outreach' })}
          >
            {intl.formatMessage(messages.mailboxes)}
          </SMLButton>
        </>
      }
    >
      {content()}
    </OutreachShell>
  );
};

export const OutreachHomeScreen = TypedMemo(OutreachHomeScreenComponent);
